import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import SuperAdminService from '../../services/SuperAdminService';
import SuperAdminPermissionMigration from './SuperAdminPermissionMigration';
import SuperAdminTaskMigration from './SuperAdminTaskMigration';
import SuperAdminLayout from './SuperAdminLayout';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';

interface PersonnelActivity {
  userId: string;
  lastLogin: number;
  loginCount: number;
  lastPlatform: string;
  companyId?: string;
  isActive?: boolean;
  createdAt?: number;
  userInfo?: {
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string;
    companyId: string;
    companyName: string;
    branchesId: string;
    role: string;
    phone?: string;
  } | null;
}

const SuperAdminPersonnel: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0); // 0: Kullanıcılar, 1: Personeller, 2: Aktif Personeller
  // ⚠️ GEÇİCİ — Yetki & Departman Taşıma modalı
  const [permMigrationOpen, setPermMigrationOpen] = useState(false);
  const [taskMigrationOpen, setTaskMigrationOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<PersonnelActivity[]>([]);
  const [activePersonnel, setActivePersonnel] = useState<PersonnelActivity[]>([]);
  // Görünüm: kart (mevcut) / liste (numaralı tablo)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  // Liste satırına tıklayınca aynı kart yapısı bir dialog içinde açılır
  const [selectedPersonnel, setSelectedPersonnel] = useState<PersonnelActivity | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [loginFilter, setLoginFilter] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Tab değiştir
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    if ((newValue === 0 || newValue === 1) && allUsers.length === 0) {
      loadUsers();
    } else if (newValue === 2 && activePersonnel.length === 0) {
      loadActivePersonnel();
    }
  };

  // İlk yüklemede ilgili veriyi yükle
  useEffect(() => {
    if ((currentTab === 0 || currentTab === 1) && allUsers.length === 0) {
      loadUsers();
    } else if (currentTab === 2 && activePersonnel.length === 0) {
      loadActivePersonnel();
    }
  }, [currentTab]);

  // Tüm sistem kullanıcılarını yükle (SystemUsers — Kullanıcılar & Personeller sekmeleri)
  const loadUsers = async () => {
    try {
      setLoading(true);
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.getAllUsers();
      if (result.success && result.data) {
        setAllUsers(result.data as PersonnelActivity[]);
        console.log(`✅ [Kullanıcılar] ${result.data.length} kullanıcı yüklendi`);
      }
    } catch (error) {
      console.error('❌ [Kullanıcılar] Yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Aktif personelleri yükle (PersonnelLoginActivities — son 7 gün)
  const loadActivePersonnel = async () => {
    try {
      setLoading(true);
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.getAllPersonnelActivities();
      if (result.success && result.data) {
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        const activePersonnelData = (result.data as PersonnelActivity[]).filter(p => p.lastLogin >= sevenDaysAgo);
        setActivePersonnel(activePersonnelData);
        console.log(`✅ [Aktif Personeller] ${activePersonnelData.length} aktif personel (son 7 gün)`);
      }
    } catch (error) {
      console.error('❌ [Aktif Personeller] Yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabPersonnel = (() => {
    if (currentTab === 0) return allUsers;
    if (currentTab === 1) return allUsers.filter(u => !!(u.companyId || u.userInfo?.companyId));
    return activePersonnel;
  })();

  const companyOptions = Array.from(
    new Set(
      tabPersonnel
        .map((p) => p.userInfo?.companyName)
        .filter((name): name is string => !!name)
    )
  ).sort((a, b) => a.localeCompare(b, 'tr'));

  const roleOptions = Array.from(
    new Set(
      tabPersonnel
        .map((p) => p.userInfo?.role)
        .filter((role): role is string => !!role)
    )
  ).sort((a, b) => a.localeCompare(b, 'tr'));

  const roleLabel = (role: string) => {
    if (role === 'employee') return 'Personel';
    if (role === 'admin') return 'Admin';
    return role;
  };

  // Personelleri filtrele (arama, tab, rol, son kullanım, şirket)
  const filteredPersonnel = (() => {
    const now = Date.now();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayStartMs = todayStart.getTime();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    return tabPersonnel.filter((personnel) => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          personnel.userId.toLowerCase().includes(searchLower) ||
          personnel.companyId?.toLowerCase().includes(searchLower) ||
          personnel.userInfo?.firstName?.toLowerCase().includes(searchLower) ||
          personnel.userInfo?.lastName?.toLowerCase().includes(searchLower) ||
          personnel.userInfo?.email?.toLowerCase().includes(searchLower) ||
          personnel.userInfo?.employeeId?.toLowerCase().includes(searchLower) ||
          personnel.userInfo?.companyName?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (roleFilter && (personnel.userInfo?.role || '') !== roleFilter) return false;
      if (companyFilter && (personnel.userInfo?.companyName || '') !== companyFilter) return false;

      if (loginFilter === 'today') {
        if (!personnel.lastLogin || personnel.lastLogin < todayStartMs) return false;
      } else if (loginFilter === '7d') {
        if (!personnel.lastLogin || personnel.lastLogin < sevenDaysAgo) return false;
      } else if (loginFilter === '30d') {
        if (!personnel.lastLogin || personnel.lastLogin < thirtyDaysAgo) return false;
      } else if (loginFilter === 'inactive') {
        if (personnel.lastLogin && personnel.lastLogin >= thirtyDaysAgo) return false;
      }

      return true;
    });
  })();

  const personnelName = (p: PersonnelActivity) =>
    `${p.userInfo?.firstName || ''} ${p.userInfo?.lastName || ''}`.trim() || p.userId;

  const displayedPersonnel = [...filteredPersonnel].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmpText = (left: string, right: string) => left.localeCompare(right, 'tr', { sensitivity: 'base' }) * dir;
    const cmpNumLast = (left?: number | null, right?: number | null) => {
      const aMissing = left == null || left === 0;
      const bMissing = right == null || right === 0;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return ((left as number) - (right as number)) * dir;
    };

    if (sortBy === 'name') return cmpText(personnelName(a), personnelName(b));
    if (sortBy === 'company') return cmpText(a.userInfo?.companyName || '', b.userInfo?.companyName || '');
    if (sortBy === 'role') return cmpText(roleLabel(a.userInfo?.role || ''), roleLabel(b.userInfo?.role || ''));
    if (sortBy === 'lastLogin') return cmpNumLast(a.lastLogin || null, b.lastLogin || null);
    if (sortBy === 'loginCount') return ((a.loginCount || 0) - (b.loginCount || 0)) * dir;
    if (sortBy === 'status') {
      const aActive = a.isActive === false ? 0 : 1;
      const bActive = b.isActive === false ? 0 : 1;
      return (aActive - bActive) * dir;
    }
    return 0;
  });

  // Platform ikonu
  const getPlatformIcon = (platform: string) => {
    if (platform.includes('mobile')) return '📱';
    if (platform.includes('web')) return '💻';
    return '🌐';
  };

  // Platform rengi
  const getPlatformColor = (platform: string) => {
    if (platform.includes('android')) return '#3DDC84';
    if (platform.includes('ios')) return '#000000';
    if (platform.includes('web')) return '#1976d2';
    return '#757575';
  };

  // Zaman farkını hesapla
  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (days > 0) return `${days} gün önce`;
    if (hours > 0) return `${hours} saat önce`;
    if (minutes > 0) return `${minutes} dakika önce`;
    return 'Az önce';
  };

  const personnelColumns = useMemo<DataTableColumn<PersonnelActivity>[]>(() => [
    {
      id: 'actions',
      label: 'İşlemler',
      width: '80px',
      align: 'center',
      render: (row) => (
        <Tooltip title="Detay">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPersonnel(row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      id: 'rowNumber',
      label: 'SN',
      width: '56px',
      align: 'center',
      render: (_row, index) => index + 1,
    },
    {
      id: 'name',
      label: 'Ad Soyad',
      minWidth: '160px',
      tooltip: (row) => personnelName(row),
      cellSx: { fontWeight: 500 },
      render: (row) => personnelName(row),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: '180px',
      tooltip: (row) => row.userInfo?.email || '',
      render: (row) => row.userInfo?.email || '-',
    },
    {
      id: 'company',
      label: 'Şirket',
      minWidth: '160px',
      tooltip: (row) => row.userInfo?.companyName || '',
      render: (row) => row.userInfo?.companyName || '-',
    },
    {
      id: 'role',
      label: 'Rol',
      render: (row) => {
        const role = row.userInfo?.role;
        if (!role) return '-';
        return (
          <Chip
            label={roleLabel(role)}
            size="small"
            sx={{ bgcolor: role === 'admin' ? '#ff9800' : '#4caf50', color: 'white', fontWeight: 500, fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      id: 'lastLogin',
      label: 'Son Giriş',
      render: (row) => (row.lastLogin ? getTimeAgo(row.lastLogin) : 'Hiç giriş yapmadı'),
    },
    {
      id: 'loginCount',
      label: 'Giriş Sayısı',
      align: 'center',
      render: (row) => row.loginCount || 0,
    },
    {
      id: 'platform',
      label: 'Platform',
      render: (row) => row.lastPlatform || '-',
    },
    {
      id: 'status',
      label: 'Durum',
      align: 'center',
      render: (row) => (
        <Chip
          label={row.isActive === false ? 'Pasif' : 'Aktif'}
          size="small"
          color={row.isActive === false ? 'error' : 'success'}
          variant={row.isActive === false ? 'filled' : 'outlined'}
        />
      ),
    },
  ], []);

  // Personel kartı
  const PersonnelCard: React.FC<{ personnel: PersonnelActivity }> = ({ personnel }) => {
    const userInfo = personnel.userInfo;
    const fullName = userInfo ? `${userInfo.firstName} ${userInfo.lastName}`.trim() : 'Bilinmeyen';
    
    return (
      <Card 
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{ 
                width: 56, 
                height: 56, 
                bgcolor: '#1976d2',
                mr: 2
              }}
            >
              {fullName !== 'Bilinmeyen' 
                ? fullName.charAt(0).toUpperCase() 
                : <PersonIcon sx={{ fontSize: 32 }} />
              }
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {fullName}
                </Typography>
                {personnel.isActive === false && (
                  <Chip
                    label="Pasif"
                    size="small"
                    sx={{
                      bgcolor: '#f44336',
                      color: 'white',
                      fontWeight: 500,
                      fontSize: '0.65rem',
                      height: 20
                    }}
                  />
                )}
              </Box>
              {userInfo?.email && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  📧 {userInfo.email}
                </Typography>
              )}
              {userInfo?.employeeId && (
                <Chip
                  label={`ID: ${userInfo.employeeId}`}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976d2',
                    fontSize: '0.65rem',
                    height: 20
                  }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Şirket Bilgisi */}
            {userInfo?.companyName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  🏢 Şirket:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                  {userInfo.companyName}
                </Typography>
              </Box>
            )}

            {/* Rol */}
            {userInfo?.role && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  👤 Rol:
                </Typography>
                <Chip 
                  label={userInfo.role === 'employee' ? 'Personel' : userInfo.role === 'admin' ? 'Admin' : userInfo.role}
                  size="small"
                  sx={{ 
                    bgcolor: userInfo.role === 'admin' ? '#ff9800' : '#4caf50',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.7rem'
                  }}
                />
              </Box>
            )}

            {/* Son Giriş */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Son Giriş
                </Typography>
                {personnel.lastLogin ? (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{getTimeAgo(personnel.lastLogin)}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(personnel.lastLogin).toLocaleString('tr-TR')}</Typography>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>Hiç giriş yapmadı</Typography>
                )}
              </Box>
            </Box>

            {/* Giriş Sayısı */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                📊 Giriş Sayısı:
              </Typography>
              <Chip 
                label={personnel.loginCount}
                size="small"
                sx={{ 
                  bgcolor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 'bold'
                }}
              />
            </Box>

            {/* Platform */}
            {personnel.lastPlatform && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {getPlatformIcon(personnel.lastPlatform)} Platform:
                </Typography>
                <Chip 
                  label={personnel.lastPlatform}
                  size="small"
                  sx={{ 
                    bgcolor: getPlatformColor(personnel.lastPlatform),
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.7rem'
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <SuperAdminLayout title="Personeller" icon={<PeopleAltIcon />}>
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              variant="outlined"
              placeholder="Personel ara (İsim, Email, Şirket, ID)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              bgcolor: '#1976d2', 
              color: 'white',
              px: 2,
              py: 1.5,
              borderRadius: 2,
              minWidth: { xs: '100%', sm: 160 }
            }}>
              <PeopleAltIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Toplam: {displayedPersonnel.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Sağ: taşıma butonları + kart/liste ikonları (görev taşımanın altında) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<SecurityIcon />}
              onClick={() => setPermMigrationOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Yetki &amp; Departman Taşıma
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<AssignmentIcon />}
              onClick={() => setTaskMigrationOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Görev Taşıma
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              color={viewMode === 'card' ? 'primary' : 'default'}
              onClick={() => setViewMode('card')}
              sx={{
                bgcolor: viewMode === 'card' ? '#1976d2' : 'transparent',
                color: viewMode === 'card' ? 'white' : 'inherit',
                '&:hover': { bgcolor: viewMode === 'card' ? '#1565c0' : 'rgba(0,0,0,0.04)' }
              }}
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              color={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
              sx={{
                bgcolor: viewMode === 'list' ? '#1976d2' : 'transparent',
                color: viewMode === 'list' ? 'white' : 'inherit',
                '&:hover': { bgcolor: viewMode === 'list' ? '#1565c0' : 'rgba(0,0,0,0.04)' }
              }}
            >
              <ViewListIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <InputLabel>Rol</InputLabel>
          <Select
            value={roleFilter}
            label="Rol"
            onChange={(e) => setRoleFilter(e.target.value)}
            MenuProps={{ disableScrollLock: true }}
          >
            <MenuItem value="">Tümü</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role} value={role}>{roleLabel(role)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <InputLabel>Son kullanım</InputLabel>
          <Select
            value={loginFilter}
            label="Son kullanım"
            onChange={(e) => setLoginFilter(e.target.value)}
            MenuProps={{ disableScrollLock: true }}
          >
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="today">Bugün</MenuItem>
            <MenuItem value="7d">Son 7 gün</MenuItem>
            <MenuItem value="30d">Son 30 gün</MenuItem>
            <MenuItem value="inactive">30+ gün / hiç giriş yok</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          options={companyOptions}
          value={companyFilter || null}
          onChange={(_, newValue) => setCompanyFilter(newValue || '')}
          renderInput={(params) => <TextField {...params} label="Şirket" placeholder="Şirket seç..." />}
          sx={{ width: { xs: '100%', sm: 280 } }}
          clearOnEscape
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <InputLabel>Sırala</InputLabel>
          <Select
            value={sortBy}
            label="Sırala"
            onChange={(e) => setSortBy(e.target.value)}
            MenuProps={{ disableScrollLock: true }}
          >
            <MenuItem value="name">Ad Soyad</MenuItem>
            <MenuItem value="company">Şirket</MenuItem>
            <MenuItem value="role">Rol</MenuItem>
            <MenuItem value="lastLogin">Son giriş</MenuItem>
            <MenuItem value="loginCount">Giriş sayısı</MenuItem>
            <MenuItem value="status">Durum</MenuItem>
          </Select>
        </FormControl>
        <IconButton
          size="small"
          onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          title={sortDir === 'asc' ? 'Artan' : 'Azalan'}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </IconButton>
        {(roleFilter || loginFilter || companyFilter) && (
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              setRoleFilter('');
              setLoginFilter('');
              setCompanyFilter('');
            }}
          >
            Filtreleri Temizle
          </Button>
        )}
      </Paper>

      {/* Personel Kategorileri Tab'ları */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: '#1976d2',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
              height: 3,
            },
          }}
        >
          <Tab 
            label="Kullanıcılar"
            icon={<PeopleAltIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Personeller"
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab 
            label="Aktif Personeller (Son 7 Gün)"
            icon={<AccessTimeIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* İçerik */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Yükleniyor...
          </Typography>
        </Box>
      ) : displayedPersonnel.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          mt: 8,
          p: 4
        }}>
          <PeopleAltIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 1, color: 'text.secondary' }}>
            {currentTab === 0 ? 'Kullanıcı Bulunamadı' : currentTab === 1 ? 'Personel Bulunamadı' : 'Aktif Personel Bulunamadı'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            {currentTab === 2
              ? 'Son 7 günde giriş yapan personel bulunamadı.'
              : (searchTerm
                  ? 'Arama kriterlerinize uygun kayıt bulunamadı.'
                  : (currentTab === 1 ? 'Şirkete bağlı kullanıcı bulunmuyor.' : 'Henüz sistemde kayıtlı kullanıcı bulunmuyor.'))}
          </Typography>
        </Box>
      ) : viewMode === 'card' ? (
        <Box sx={{ pb: 4 }}>
          <Grid container spacing={2}>
            {displayedPersonnel.map((personnel) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={3} 
                xl={2} 
                key={personnel.userId}
              >
                <PersonnelCard personnel={personnel} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ pb: 4 }}>
          <AccountingDataTable<PersonnelActivity>
            columns={personnelColumns}
            rows={displayedPersonnel}
            getRowId={(row) => row.userId}
            onRowClick={(row) => setSelectedPersonnel(row)}
            clickable
            tableLayout="auto"
            ariaLabel="Personeller"
            defaultRowsPerPage={25}
            rowsPerPageOptions={[25, 50, 100]}
            emptyMessage="Kayıt bulunamadı."
          />
        </Box>
      )}

      {/* Liste satırına tıklayınca kart yapısı dialog içinde açılır */}
      <Dialog
        open={!!selectedPersonnel}
        onClose={() => setSelectedPersonnel(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          {selectedPersonnel && <PersonnelCard personnel={selectedPersonnel} />}
        </DialogContent>
      </Dialog>

      {/* ⚠️ GEÇİCİ — Yetki & Departman Taşıma modalı */}
      <Dialog
        open={permMigrationOpen}
        onClose={() => setPermMigrationOpen(false)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogContent sx={{ p: 0 }}>
          <SuperAdminPermissionMigration onClose={() => setPermMigrationOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* ⚠️ GEÇİCİ — Görev Taşıma modalı */}
      <Dialog
        open={taskMigrationOpen}
        onClose={() => setTaskMigrationOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <SuperAdminTaskMigration onClose={() => setTaskMigrationOpen(false)} />
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
};

export default SuperAdminPersonnel;
